//
// V2 visual-identity variants. Each variant defines the dark-surface tokens
// and the two unified gradients (hero + signupPanel) that drive the look of
// the `/` landing page and the `/signup` marketing panel.
//
// To flip the entire v2 visual identity, change `ACTIVE_VARIANT_NAME` below.
// Pages do not need to know about variants — they read tokens via
// `theme.palette.v2.surface.*` and `theme.palette.v2.gradients.*` and pick up
// the active variant automatically.
//
// Invariant: within a variant, `gradients.hero === gradients.signupPanel` so
// the landing hero and the signup marketing panel always feel like one brand.
//
// NOTE on CTAs: `gradients.cta` and the contained-secondary button styling are
// intentionally NOT variant-driven — yellow is the brand accent in every
// variant. The variants change the *background* identity of the dark surfaces;
// the call-to-action remains yellow on either backdrop.
//

import { indigo, yellow } from './palettes';

// Black canvas with warm yellow radial accents — the original askneo brand.
const blackYellowGradient =
  'radial-gradient(ellipse at 78% 18%, rgba(255, 235, 59, 0.22) 0%, transparent 55%), '
  + 'radial-gradient(circle at 18% 90%, rgba(255, 214, 10, 0.10) 0%, transparent 40%), '
  + 'linear-gradient(160deg, #050505 0%, #171005 100%)';

// Deep indigo → mid indigo, mirroring the current signup right-panel.
const darkBlueGradient = `linear-gradient(160deg, ${indigo[900]} 0%, ${indigo[700]} 100%)`;

const blackYellow = {
  surface: {
    dark: '#0A0805',
    band: '#15110A',
    glass: 'rgba(255, 235, 59, 0.06)',
    glassBorder: 'rgba(255, 235, 59, 0.16)',
    heroGlow: yellow[500],
    heroImageShadow: 'rgba(255, 235, 59, 0.10)'
  },
  gradients: {
    hero: blackYellowGradient,
    signupPanel: blackYellowGradient
  }
};

const darkBlue = {
  surface: {
    dark: indigo[900],
    band: '#252C66',
    glass: 'rgba(166, 179, 245, 0.10)',
    glassBorder: 'rgba(166, 179, 245, 0.24)',
    heroGlow: indigo[300],
    heroImageShadow: 'rgba(166, 179, 245, 0.12)'
  },
  gradients: {
    hero: darkBlueGradient,
    signupPanel: darkBlueGradient
  }
};

export const themeVariants = {
  blackYellow,
  darkBlue
};

// ─── Change this one line to flip the v2 visual identity everywhere ──────────
export const ACTIVE_VARIANT_NAME = 'blackYellow'; // 'blackYellow' | 'darkBlue'
// ─────────────────────────────────────────────────────────────────────────────

// Resolve once. If the name is mistyped, fall back to the default variant so
// the module still loads — a louder error here would crash the whole v2 surface.
const resolved = themeVariants[ACTIVE_VARIANT_NAME];
if (!resolved && process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.warn(
    `[themeVariants] ACTIVE_VARIANT_NAME="${ACTIVE_VARIANT_NAME}" is not a known variant. `
    + `Falling back to "blackYellow". Known variants: ${Object.keys(themeVariants).join(', ')}.`
  );
}
export const activeVariant = resolved || themeVariants.blackYellow;
